import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AlertMessage from "../../../../components/AlertMessage";
import { GenericSaveButton } from "../../../../components/common/GenericSaveButton";
import { useAlert } from "../../../../hooks/useAlert";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { getTitle } from "../../../../utils/formatters/StringFormatter";
import { RouteNavigationService } from "../../../../utils/navegation/RouteNavegationService";
import { validateAllRequiredLang } from "../../../../utils/validators/LanguageValidators";
import { editData, getFilters, saveData } from "../api/basicGenericTable";
import DictionaryForm from "../components/DictionaryForm";
import { DictionaryFormDto, FormErrors } from "../types";
import { useQueryClient } from "@tanstack/react-query";

export default function DictionaryFormPage() {
    const { t } = useTranslation(["common", "genericDic"]);
    const navigate = useNavigate();
    const location = useLocation();
    const { table, id } = useParams<{ table: string; id: string }>();
    const tableName = table ? table.charAt(0).toLowerCase() + table.slice(1) : "";
    const stateHasCode = (location.state as { hasCode?: boolean } | null)?.hasCode;
    const hasCode = stateHasCode ?? (tableName === "functionalAreas" || tableName === "serviceIndicators");
    const isEditMode = Boolean(id);
    const [nextEntryIdPreview, setNextEntryIdPreview] = useState<number | null>(null);
    const queryClient = useQueryClient();
    const [formData, setFormData] = useState<DictionaryFormDto>({
        id: 0,
        description: {
            catalan: "",
            english: "",
            french: "",
            spanish: ""
        }
    });
    
    useEffect(() => {
        if (isEditMode || !tableName) return;

        getFilters(tableName)
            .then((filters) => {
                const ids = (filters.values ?? [])
                    .map((item) => Number(item.key))
                    .filter((value) => Number.isInteger(value) && value > 0);

                const maxId = ids.length > 0 ? Math.max(...ids) : 0;
                setNextEntryIdPreview(maxId + 1);
            })
            .catch(() => {
                setNextEntryIdPreview(null);
            });
    }, [isEditMode, tableName]);

    const [errors, setErrors] = useState<FormErrors>({ description: {} });
    const { alert, hideAlert, isVisible, showAlert } = useAlert({ fromLocation: false });

    const handleSaveEvent = async () => {
        const validationErrors = validateAllRequiredLang(formData, t);
        setErrors(validationErrors);

        if (Object.keys(validationErrors.description ?? {}).length > 0) {
            return;
        }

        try {
            if (isEditMode) {
                await editData(formData, tableName);
            } else {
                await saveData(formData, tableName);
            }

            const navigationService = new RouteNavigationService(navigate);
            const queryKeyName = 't(tableName)' + 'select';
            queryClient.invalidateQueries({
                queryKey: [queryKeyName],
                exact: false,
                refetchType: 'all',
            });
            navigationService.navigateWithMessage(
                `/admin/${tableName}`,
                isEditMode
                    ? t("genericDic:modal.diccionariSuccessFormEdit", { table: t(tableName) })
                    : t("genericDic:modal.diccionariSuccessForm", { table: t(tableName) })
            );
        } catch (uploadError) {
            const fetchError = uploadError as ApiErrorResponse;
            const errorMessage = fetchError?.message;
            showAlert({
                message: errorMessage?.toString() ?? "",
                type: "danger",
                duration: 8000
            });
        }
    };

    const onGoBackClick = async () => {
        navigate(`/admin/${table}`);
    }

    return (
        <>
            <Row className="flex-grow-1 align-items-stretch" style={{ minHeight: 0 }}>
                <Col xs={8} md={8}>
                    {isEditMode ? (
                        <h2>{t(`genericDic:${tableName}`)} # {id}</h2>
                    ) : (
                        <h2>{getTitle(t, tableName)}</h2>
                    )}
                </Col>
                <Col xs={4} md={4} className="text-end">
                    <GenericSaveButton
                        onClick={handleSaveEvent}
                        onCancelClick={() => onGoBackClick()}
                        cancelLabel={t(isEditMode ? "common:buttons.buttonBack" : "common:buttons.buttonCancel")}
                        minWidth="180px"
                        className="ms-2"
                    />
                </Col>
                {isVisible && alert && (
                    <AlertMessage
                        message={alert.message}
                        type={alert.type}
                        onClose={hideAlert}
                    />
                )}
            </Row>
            <Row>
                <Col>
                    <DictionaryForm
                        formData={formData}
                        setFormData={setFormData}
                        errors={errors}
                        table={tableName}
                        hasCode={hasCode}
                        nextEntryIdPreview={nextEntryIdPreview}
                    />
                </Col>
            </Row>
        </>
    );
}


